/**
 * Subscription Frequency Updater
 * Updates the "Ships every" dropdown when variant changes occur
 * Loaded only on specific product templates that use the subscription snippet
 */

class SubscriptionFrequencyUpdater {
  constructor() {
    this.frequencySelect = null;
    this.currentVariant = null;
    this.initialized = false;
    this.init();
  }

  init() {
    if (this.initialized) return;
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Find the frequency select element
    this.frequencySelect = document.querySelector('.frequency-select');
    
    if (!this.frequencySelect) {
      console.warn('Subscription Frequency Updater: frequency-select element not found');
      return;
    }

    // Listen for variant change events
    document.addEventListener('variant:change', (event) => {
      this.handleVariantChange(event);
    });

    // Listen for frequency select changes
    this.frequencySelect.addEventListener('change', (event) => {
      this.handleFrequencyChange(event);
    });

    this.initialized = true;
    console.log('Subscription Frequency Updater: Initialized successfully');
    
    // Update prices for initial load
    this.updateInitialPrices();
  }

  updateInitialPrices() {
    // Get the current variant from the page
    const selectedVariantInput = document.querySelector('[data-variant-input]:checked');
    if (selectedVariantInput) {
      const variantTitle = selectedVariantInput.value;
      const variant = { title: variantTitle, option1: variantTitle };
      this.updateCustomPrices(variant);
      this.updateFreeBoxVisibility(variant);
    }
  }

  handleFrequencyChange(event) {
    const newSellingPlanId = event.target.value;
    const subscriptionContainer = document.querySelector('.wd-stay-ai-subscription');
    
    if (subscriptionContainer && subscriptionContainer.getAttribute('data-subscription') === 'true') {
      subscriptionContainer.setAttribute('data-selling-plan', newSellingPlanId);
      console.log('Subscription Frequency Updater: Updated data-selling-plan to', newSellingPlanId);
    }
  }

  handleVariantChange(event) {
    if (!event.detail || !event.detail.variant) {
      console.warn('Subscription Frequency Updater: Invalid variant change event');
      return;
    }

    const newVariant = event.detail.variant;
    this.currentVariant = newVariant;

    // Update the frequency dropdown options
    this.updateFrequencyOptions(newVariant);
    
    // Update custom prices for specific packs
    this.updateCustomPrices(newVariant);
    
    // Update free box visibility based on variant
    this.updateFreeBoxVisibility(newVariant);
  }

  updateFrequencyOptions(variant) {
    if (!this.frequencySelect || !variant) {
      return;
    }

    // Check if variant has selling plan allocations
    if (!variant.selling_plan_allocations || variant.selling_plan_allocations.length === 0) {
      console.warn('Subscription Frequency Updater: Variant has no selling plan allocations');
      return;
    }

    // Store the current selected value
    const currentSelectedValue = this.frequencySelect.value;
    
    // Clear existing options
    this.frequencySelect.innerHTML = '';

    // Add new options based on the variant's selling plan allocations
    variant.selling_plan_allocations.forEach((allocation, index) => {
      const option = document.createElement('option');
      option.value = allocation.selling_plan_id;
      option.textContent = allocation.name || allocation.options?.[0]?.value || `Plan ${index + 1}`;
      
      // Extract the frequency text from the selling plan
      const frequencyText = this.getFrequencyText(allocation);
      option.textContent = frequencyText;
      
      // Try to maintain the same selection if the plan still exists
      if (allocation.selling_plan_id == currentSelectedValue) {
        option.selected = true;
      } else if (index === 0 && !this.hasMatchingPlan(variant.selling_plan_allocations, currentSelectedValue)) {
        // Select first option if current selection doesn't exist in new variant
        option.selected = true;
      }
      
      this.frequencySelect.appendChild(option);
    });

    // Trigger change event to update any dependent UI
    this.frequencySelect.dispatchEvent(new Event('change', { bubbles: true }));
    
    // Update data-selling-plan attribute if subscription is active
    const subscriptionContainer = document.querySelector('.wd-stay-ai-subscription');
    if (subscriptionContainer && subscriptionContainer.getAttribute('data-subscription') === 'true') {
      subscriptionContainer.setAttribute('data-selling-plan', this.frequencySelect.value);
    }
    
    console.log('Subscription Frequency Updater: Updated frequency options for variant', variant.id);
  }

  /**
   * Extract frequency text from selling plan allocation
   * @param {Object} allocation - The selling plan allocation object
   * @return {string} - The frequency text to display
   */
  getFrequencyText(allocation) {
    // Try to get from the selling plan name first
    if (allocation.name) {
      return allocation.name.replace('Delivery every ', '').replace('Deliver every ', '');
    }

    // Try to get from options array
    if (allocation.options && allocation.options.length > 0) {
      // Look for the first option (usually the frequency)
      const frequencyOption = allocation.options[0];
      if (frequencyOption && frequencyOption.value) {
        return frequencyOption.value;
      }
    }

    // Fallback to selling plan ID
    return `Plan ${allocation.selling_plan_id}`;
  }

  hasMatchingPlan(allocations, planId) {
    return allocations.some(allocation => allocation.selling_plan_id == planId);
  }

  // Method to manually trigger an update if needed
  forceUpdate() {
    if (this.currentVariant) {
      this.updateFrequencyOptions(this.currentVariant);
    }
  }

  updateCustomPrices(variant) {
    const priceMap = {
      '1 PACK': { sale: '$18', real: '$18', percentage: '' },
      '4 PACK': { sale: '$119', real: '$69.95', percentage: '40%' },
      '6 PACK': { sale: '$155', real: '$79.95', percentage: '50%' }
    };
    
    const variantTitle = variant.title || variant.option1;
    if (!variantTitle) return;
    
    const pack = variantTitle.toUpperCase();
    const prices = priceMap[pack];
    if (!prices) return;
    
    const pricesContainer = document.querySelector('.pricesContainer');
    if (pricesContainer) {
      const saleSpan = pricesContainer.querySelector('.sale_price');
      const realSpan = pricesContainer.querySelector('.price');
      if (saleSpan) saleSpan.textContent = prices.sale;
      if (realSpan) realSpan.textContent = prices.real;
    }
    
    // Update savings percentage
    const savingsPercentSpan = document.querySelector('.savings-percent');
    if (savingsPercentSpan) {
      savingsPercentSpan.textContent = prices.percentage;
    }
  }

  updateFreeBoxVisibility(variant) {
    // Get the free box bullet element
    const freeBoxBullets = document.querySelectorAll('.info-bullets__bullet.free-box');
    if (!freeBoxBullets) return;
    
    const variantTitle = variant.title || variant.option1;
    if (!variantTitle) return;
    
    const pack = variantTitle.toUpperCase();
    
    // Show the free box bullet for 4 PACK or 6 PACK variants
    if (pack === '4 PACK' || pack === '6 PACK') {
      freeBoxBullets.forEach(bullet => {
        bullet.style.display = 'flex';
      });
    } else {
      freeBoxBullets.forEach(bullet => {
        bullet.style.display = 'none';
      });
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Only initialize if the subscription element exists
  const subscriptionElement = document.querySelector('.wd-stay-ai-subscription');
  if (subscriptionElement) {
    window.subscriptionFrequencyUpdater = new SubscriptionFrequencyUpdater();
  }
}); 